import React, { useState, useRef, useEffect, useCallback } from "react";
import "../assets/styles/chat.css";

const AdvancedChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [conversationContext, setConversationContext] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
    </svg>
  );

  const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );

  const UserIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );

  const BotIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  );

  const ThumbsUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
    </svg>
  );

  const ThumbsDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
    </svg>
  );

  const knowledgeBase = {
    // Greetings and casual conversation
    greeting: {
      keywords: ["hi", "hello", "hey", "greetings", "good morning", "good afternoon", "good evening", "sup", "yo", "hola", "howdy", "what's up", "whats up", "heya", "hi there", "hello there"],
      responses: [
        "Hey! I'm Ushan 👋 Nice to meet you! I'm a full-stack developer passionate about creating amazing web experiences. What would you like to know about me?",
        "Hi there! I'm Ushan Mihiranga 🚀 A software engineer who loves building cool stuff with React and Node.js. How can I help you today?",
        "Hello! I'm Ushan ✨ Currently working as a Full Stack Developer at FonixEdu Pvt Ltd by Atlas Axillia and studying Software Engineering at SLIIT. What brings you here?",
        "Hey! I'm Ushan 😊 I build web applications, mobile apps, and AI solutions. Feel free to ask me anything!",
        "Greetings! 🌟 I'm Ushan, a passionate developer specializing in full-stack development. I'd love to share my journey with you!",
        "Hello there! 💻 I'm Ushan - your friendly neighborhood developer! Ask me about my projects, skills, or anything else!",
      ],
    },
    howAreYou: {
      keywords: ["how are you", "how's it going", "what's up", "how do you do", "how are things", "hows it going", "whats up", "how r u", "how are u", "how's everything", "hows everything"],
      responses: [
        "I'm doing great, thanks for asking! 😊 I'm excited to share about my journey in software development. What would you like to know?",
        "Fantastic! Just finished working on some exciting projects. How about you? What can I help you with today?",
        "I'm doing well! Always learning something new in tech. What brings you here today?",
        "Awesome! 🚀 Currently juggling work and studies, but loving every moment of it! What can I tell you about Ushan?",
        "Pretty great! 💪 Just wrapped up some cool features at work. Want to hear about them?",
      ],
    },
    thanks: {
      keywords: ["thank", "thanks", "appreciate", "grateful", "thx", "ty", "thank you", "thanks a lot", "many thanks", "cheers", "awesome", "cool"],
      responses: [
        "You're welcome! 😊 Feel free to ask if you need anything else!",
        "Happy to help! Let me know if you have more questions!",
        "Anytime! That's what I'm here for! 🚀",
        "My pleasure! Don't hesitate to reach out if you need more info!",
        "Glad I could help! 🌟 Is there anything else you'd like to know?",
        "No problem at all! 💯 I'm here whenever you need information!",
      ],
    },
    goodbye: {
      keywords: ["bye", "goodbye", "see you", "later", "farewell", "take care"],
      responses: [
        "Goodbye! Feel free to come back anytime! 👋",
        "See you later! Don't hesitate to reach out if you need anything! 🚀",
        "Take care! Hope to chat with you again soon! ✨",
      ],
    },

    // Personal information
    personal: {
      keywords: ["who", "about", "yourself", "introduction", "name", "tell me about you", "who are you", "introduce yourself", "tell me more", "about yourself", "your name", "who is ushan"],
      responses: [
        "I'm Ushan Mihiranga! 🎯 A passionate third-year Software Engineering student at SLIIT and a Full Stack Developer at FonixEdu Pvt Ltd by Atlas Axillia. I love creating innovative solutions that make a difference. I specialize in React.js, Node.js, and building seamless user experiences!",
        "Hey! I'm Ushan - a full-stack developer who's all about turning ideas into reality through code! 💻 Currently juggling my studies at SLIIT with my role at FonixEdu Pvt Ltd by Atlas Axillia. I'm passionate about web development, AI, and creating user-friendly applications!",
        "Nice to meet you! I'm Ushan Mihiranga ✨ A software engineer with a passion for building scalable web applications. I work with modern tech stacks like MERN, and I'm always exploring new technologies!",
      ],
    },
    age: {
      keywords: ["age", "old", "born", "birthday"],
      responses: [
        "I'm a young and enthusiastic developer currently in my third year of university! 🎓 Age is just a number - what matters is the passion for coding and continuous learning!",
      ],
    },
    hobbies: {
      keywords: ["hobby", "hobbies", "free time", "interests", "fun", "enjoy"],
      responses: [
        "When I'm not coding, I love exploring new technologies, working on side projects, and staying updated with the latest tech trends! 🚀 I also enjoy problem-solving through competitive programming and building AI-powered solutions!",
        "I'm passionate about technology! I spend my free time learning new frameworks, contributing to open-source projects, and experimenting with AI and machine learning. I also love building chatbots like this one! 🤖",
      ],
    },

    // Skills and expertise
    skills: {
      keywords: ["skills", "technologies", "programming", "languages", "tech", "expertise", "stack", "tools", "what can you do", "technical skills", "tech stack", "what do you know", "abilities", "capabilities"],
      responses: [
        "I've got a diverse tech stack! 💪<br/><strong>Frontend:</strong> React.js, Angular, HTML5, CSS3, JavaScript (ES6+)<br/><strong>Backend:</strong> Node.js, Express.js, PHP, .NET<br/><strong>Mobile:</strong> React Native, Kotlin<br/><strong>Databases:</strong> MongoDB, MySQL, PostgreSQL<br/><strong>Languages:</strong> Java, Python, C/C++<br/><strong>Tools:</strong> Git, Docker, WebSocket, Payment Gateway Integration",
        "My technical arsenal includes modern web technologies! 🔧<br/>I'm proficient in <strong>React.js & Angular</strong> for frontend, <strong>Node.js & Express</strong> for backend, and I work with databases like <strong>MongoDB & MySQL</strong>. I also develop mobile apps using <strong>React Native & Kotlin</strong>, and I'm experienced with <strong>Git, Docker, WebSocket</strong>, and payment integrations!",
        "I'm a versatile developer with expertise across the full stack! 🎯<br/>From crafting beautiful UIs with <strong>React.js</strong> to building robust APIs with <strong>Node.js</strong>, managing data with <strong>MongoDB/MySQL</strong>, and deploying with <strong>Docker</strong> - I've got you covered!",
      ],
    },
    frontend: {
      keywords: ["frontend", "front-end", "react", "angular", "ui", "interface", "css", "html", "javascript", "user interface", "web design", "responsive", "reactjs"],
      responses: [
        "I'm really passionate about frontend development! 🎨 I work extensively with <strong>React.js</strong> - it's my go-to framework. I also have experience with <strong>Angular</strong>, and I'm proficient in <strong>HTML5, CSS3, and modern JavaScript (ES6+)</strong>. I love creating beautiful, responsive, and user-friendly interfaces!",
        "Frontend is where I shine! ✨ I specialize in <strong>React.js</strong> and have built numerous responsive web applications. I'm also skilled in <strong>Angular, HTML5, CSS3</strong>, and I focus on creating seamless user experiences with modern design principles!",
        "Creating stunning user interfaces is my passion! 💫 I build responsive, accessible, and performant web apps using <strong>React.js</strong> with modern best practices. Every pixel matters!",
      ],
    },
    backend: {
      keywords: ["backend", "back-end", "server", "api", "node", "express", "database"],
      responses: [
        "On the backend side, I work with <strong>Node.js & Express.js</strong> to build robust APIs and server-side applications! 🖥️ I also have experience with <strong>PHP and .NET</strong>. I'm comfortable working with databases like <strong>MongoDB, MySQL, and PostgreSQL</strong>, and I've integrated various third-party services including payment gateways!",
        "Backend development is my forte! 💻 I build scalable APIs using <strong>Node.js & Express.js</strong>, work with multiple databases (<strong>MongoDB, MySQL, PostgreSQL</strong>), and I've implemented features like <strong>WebSocket</strong> for real-time communication and payment gateway integrations!",
      ],
    },
    mobile: {
      keywords: ["mobile", "app", "android", "ios", "react native", "kotlin"],
      responses: [
        "I develop mobile applications using <strong>React Native</strong> for cross-platform apps and <strong>Kotlin</strong> for native Android development! 📱 I love the challenge of creating smooth, performant mobile experiences!",
      ],
    },

    // Projects
    projects: {
      keywords: ["projects", "work", "portfolio", "built", "developed", "created", "made", "what have you built", "show me your work", "your projects", "past projects", "applications"],
      responses: [
        "I've worked on some really exciting projects! 🚀<br/>🎯 <strong>FEDES AI</strong> - An emotion detection tool for educators<br/>📚 <strong>ExamHUB</strong> - A gamified education platform<br/>🏥 <strong>Health Care Management System</strong> - Complete healthcare solution<br/>🏫 <strong>School Management System</strong> - Built with MERN stack<br/>🛒 <strong>E-commerce Platforms</strong> - Multiple buying/selling solutions<br/>🤖 <strong>AI Chat Bots</strong> - Like this one you're talking to!",
        "My portfolio includes diverse projects across different domains! 💼<br/>I've built <strong>AI-powered tools</strong> for education, <strong>healthcare management systems</strong>, <strong>e-commerce platforms</strong>, and <strong>intelligent chatbots</strong>. Each project taught me something new and helped me grow as a developer!",
        "I love building solutions that solve real problems! 🎨<br/>From <strong>AI-driven educational tools</strong> to <strong>full-stack management systems</strong>, I've tackled projects in healthcare, education, and e-commerce. Want to know more about any specific project?",
      ],
    },
    fedesai: {
      keywords: ["fedes", "emotion", "detection", "ai project"],
      responses: [
        "<strong>FEDES AI</strong> is one of my favorite projects! 🎓 It's an emotion detection tool designed specifically for educators. It helps teachers understand student engagement and emotional states during online learning sessions. Built using AI/ML technologies!",
      ],
    },
    examhub: {
      keywords: ["examhub", "exam", "gaming", "education platform"],
      responses: [
        "<strong>ExamHUB</strong> is a gamified education platform! 🎮📚 It makes learning fun by incorporating gaming elements into the examination process. Students can take exams in an engaging environment that motivates them to learn better!",
      ],
    },

    // Experience
    experience: {
      keywords: ["job", "work", "experience", "company", "fonix", "career", "position", "role", "employment", "where do you work", "current job", "work experience", "professional"],
      responses: [
        "I'm currently working as a <strong>Full Stack Web Developer at FonixEdu Pvt Ltd by Atlas Axillia</strong> since February 2024! 💼<br/>My day-to-day involves:<br/>• Developing responsive landing pages and web applications<br/>• Building admin dashboards and client interfaces<br/>• Creating backend services and RESTful APIs<br/>• Integrating payment gateways and third-party services<br/>• Implementing real-time features using WebSocket<br/><br/>It's been an amazing learning experience!",
        "I've been with <strong>FonixEdu Pvt Ltd by Atlas Axillia</strong> as a Full Stack Developer since Feb 2024! 🚀 I work on the entire development lifecycle - from designing user interfaces to building robust backend systems. I've gained hands-on experience with payment integrations, WebSocket technology, and building scalable web applications!",
        "Working at <strong>FonixEdu Pvt Ltd by Atlas Axillia</strong> has been incredible! 🌟 I get to work on cutting-edge EdTech solutions, collaborate with talented developers, and continuously learn new technologies. It's the perfect environment for growth!",
      ],
    },
    fonix: {
      keywords: ["fonix", "fonixedu", "atlas axillia", "current job", "employer", "company"],
      responses: [
        "<strong>FonixEdu Pvt Ltd by Atlas Axillia</strong> is where I currently work as a Full Stack Web Developer! 💼 It's a great environment where I get to work on diverse educational technology projects. I've learned so much about professional software development and EdTech solutions here!",
      ],
    },

    // Education
    education: {
      keywords: ["education", "study", "university", "college", "degree", "sliit", "school", "academic", "studying", "student", "qualification", "where did you study"],
      responses: [
        "I'm currently pursuing my <strong>BSc (Hons) in Information Technology</strong> at SLIIT, specializing in Software Engineering! 🎓<br/>📚 I completed my A/Levels in Physical Science at St. Aloysius College, Rathnapura<br/>🏆 I'm also certified in AI-Assisted Python Programming from Niravana Clouds Academy<br/><br/>Education and continuous learning are super important to me!",
        "I'm in my third year at <strong>SLIIT</strong>, studying Software Engineering! 🎓 Before that, I did my A/Levels in Physical Science. I also keep upskilling - recently got certified in AI-Assisted Python Programming! I believe in learning beyond the classroom!",
        "Education is my foundation! 📖 I'm a third-year <strong>Software Engineering student at SLIIT</strong>, and I complement my formal education with certifications in cloud computing and AI. Learning never stops!",
      ],
    },
    sliit: {
      keywords: ["sliit university", "sri lanka institute"],
      responses: [
        "I'm studying at <strong>SLIIT (Sri Lanka Institute of Information Technology)</strong>! 🎓 It's one of the leading tech universities in Sri Lanka. I'm specializing in Software Engineering and currently in my third year. The program has given me a strong foundation in both theoretical concepts and practical skills!",
      ],
    },

    // Certifications
    certifications: {
      keywords: ["certifications", "certificates", "certified", "certification", "courses", "training", "credentials"],
      responses: [
        "I'm constantly upskilling! Here are my certifications: 🏆<br/><br/><strong>AWS SimuLearn Series:</strong><br/>• Core Security Concepts<br/>• Databases in Practice<br/>• Auto-Healing & Scaling Applications<br/>• Highly Available Web Applications<br/>• File Systems in the Cloud<br/>• Amazon S3 – Introduction<br/><br/><strong>Microsoft Azure Fundamentals:</strong><br/>• Azure Identity, Access & Security<br/>• Relational Database Services in Azure<br/>• Azure Load Balancer for Scalability & Resiliency<br/>• Azure Blob Storage Configuration<br/>• Azure Virtual Machines Disk Management<br/><br/><strong>Google Cloud & AI:</strong><br/>• Introduction to Generative AI<br/>• Introduction to Large Language Models<br/>• Introduction to Responsible AI<br/>• Responsible AI: Applying AI Principles with Google Cloud",
        "I've completed multiple cloud and AI certifications! ☁️🤖<br/><br/>I'm certified in <strong>AWS</strong> (SimuLearn Series covering security, databases, scaling, and S3), <strong>Microsoft Azure</strong> (fundamentals including identity, databases, load balancing, and storage), and <strong>Google Cloud AI</strong> (Generative AI, LLMs, and Responsible AI principles).<br/><br/>I believe in continuous learning and staying updated with the latest cloud technologies!",
      ],
    },
    aws: {
      keywords: ["aws", "amazon web services", "aws certification"],
      responses: [
        "I've completed the <strong>AWS SimuLearn Series</strong>! ☁️<br/>My AWS certifications include:<br/>• Core Security Concepts<br/>• Databases in Practice<br/>• Auto-Healing & Scaling Applications<br/>• Highly Available Web Applications<br/>• File Systems in the Cloud<br/>• Amazon S3 – Introduction<br/><br/>These have given me hands-on experience with AWS cloud services!",
      ],
    },
    azure: {
      keywords: ["azure", "microsoft azure", "azure certification"],
      responses: [
        "I'm certified in <strong>Microsoft Azure Fundamentals</strong>! ☁️<br/>My Azure certifications cover:<br/>• Azure Identity, Access & Security<br/>• Relational Database Services in Azure<br/>• Scalability & Resiliency with Azure Load Balancer<br/>• Azure Blob Storage Configuration<br/>• Azure Virtual Machines Disk Management<br/><br/>I'm comfortable working with Azure cloud infrastructure!",
      ],
    },
    googlecloud: {
      keywords: ["google cloud", "gcp", "google ai", "generative ai"],
      responses: [
        "I've completed <strong>Google Cloud & AI Certifications</strong>! 🤖<br/>My Google certifications include:<br/>• Introduction to Generative AI<br/>• Introduction to Large Language Models<br/>• Introduction to Responsible AI<br/>• Responsible AI: Applying AI Principles with Google Cloud<br/><br/>I'm passionate about AI and staying at the forefront of generative AI technology!",
      ],
    },

    // Contact
    contact: {
      keywords: ["contact", "reach", "phone", "email", "linkedin", "github", "connect", "get in touch", "how to contact", "contact info", "reach out", "talk to you", "message you"],
      responses: [
        "I'd love to connect with you! 📞<br/><strong>Phone:</strong> +94 760 673 035<br/><strong>Email:</strong> ushanmihiranga2017@gmail.com<br/><strong>Address:</strong> 168/6 First Lane, Welivita Road, Malabe<br/><strong>GitHub:</strong> github.com/Ushan2001<br/><br/>Feel free to reach out anytime! You can also download my CV if you'd like! 📄",
        "Let's connect! 🤝<br/>You can reach me at <strong>+94 760 673 035</strong> or email me at <strong>ushanmihiranga2017@gmail.com</strong>. Check out my code on <strong>GitHub: github.com/Ushan2001</strong>. I'm always open to interesting conversations and collaboration opportunities!<br/><br/>Want my CV? Just ask! 📄",
        "I'm always happy to chat! 💬<br/>Drop me an email at <strong>ushanmihiranga2017@gmail.com</strong> or call me at <strong>+94 760 673 035</strong>. You can also explore my projects on <strong>GitHub</strong>. Looking forward to hearing from you!",
      ],
    },
    cv: {
      keywords: ["cv", "resume", "curriculum vitae", "download cv", "download resume", "get cv", "get resume", "your cv", "your resume"],
      responses: [
        "I'd be happy to share my CV with you! 📄<br/><br/>You can download it here: <a href='/cv.pdf' download='Ushan_Mihiranga_CV.pdf' style='color: #4CAF50; font-weight: bold; text-decoration: underline;'>Download My CV</a><br/><br/>It includes my complete work experience, education, skills, and projects!",
        "Sure! Here's my CV for you! 📄<br/><br/><a href='/cv.pdf' download='Ushan_Mihiranga_CV.pdf' style='color: #4CAF50; font-weight: bold; text-decoration: underline;'>Click here to download my CV</a><br/><br/>Feel free to check out my qualifications, experience, and projects!",
      ],
    },
    email: {
      keywords: ["email address", "mail"],
      responses: [
        "You can email me at <strong>ushanmihiranga2017@gmail.com</strong> 📧 I usually respond within 24 hours!",
      ],
    },
    github: {
      keywords: ["github profile", "git", "code repository"],
      responses: [
        "Check out my code on GitHub! 💻 <strong>github.com/Ushan2001</strong> - You'll find various projects showcasing my skills in web development, AI, and more!",
      ],
    },

    // Specific tech questions
    react: {
      keywords: ["react.js", "reactjs", "react developer"],
      responses: [
        "React.js is my primary frontend framework! ⚛️ I use it extensively in my work at FonixEdu and in personal projects. I'm comfortable with hooks, state management, component architecture, and building performant React applications!",
      ],
    },
    nodejs: {
      keywords: ["node.js", "nodejs", "node developer"],
      responses: [
        "I love working with Node.js! 🟢 It's my go-to for backend development. I build RESTful APIs, implement WebSocket connections, integrate databases, and create scalable server-side applications using Node.js and Express.js!",
      ],
    },
    ai: {
      keywords: ["artificial intelligence", "machine learning", "ml", "ai"],
      responses: [
        "AI and Machine Learning fascinate me! 🤖 I've worked on projects like FEDES AI (emotion detection) and built various chatbots. I'm certified in AI-Assisted Python Programming and I'm always exploring new AI technologies and applications!",
      ],
    },

    // Career and aspirations
    goals: {
      keywords: ["goals", "future", "aspirations", "plans", "ambition", "dream", "what's next", "whats next", "vision", "where do you see yourself"],
      responses: [
        "My goal is to become a well-rounded software engineer who can architect and build impactful solutions! 🎯 I want to specialize in full-stack development while staying updated with emerging technologies like AI and cloud computing. Ultimately, I'd love to work on products that make a real difference in people's lives!",
        "I'm focused on continuous growth! 🚀 Short-term, I want to master advanced React patterns and cloud technologies. Long-term, I aspire to lead development teams and create innovative products. I'm particularly interested in the intersection of AI and web development!",
        "I dream big! 🌟 I want to become a tech leader who builds scalable, user-centric applications. I'm passionate about leveraging AI and cloud technologies to solve real-world problems and create meaningful impact!",
      ],
    },
    hire: {
      keywords: ["hire", "hiring", "available", "looking for work", "freelance", "job opportunity"],
      responses: [
        "I'm always open to interesting opportunities! 💼 While I'm currently working at FonixEdu Pvt Ltd by Atlas Axillia, I'm interested in freelance projects and exciting full-time opportunities that align with my skills and growth goals. Feel free to reach out at <strong>ushanmihiranga2017@gmail.com</strong>!",
      ],
    },

    // Fun and personality
    favorite: {
      keywords: ["favorite", "favourite", "prefer", "like best"],
      responses: [
        "My favorite tech stack? Definitely the MERN stack (MongoDB, Express, React, Node.js)! 💚 It allows me to work with JavaScript throughout the entire application, which I find really efficient and enjoyable!",
      ],
    },
    why: {
      keywords: ["why developer", "why programming", "why coding"],
      responses: [
        "I love being a developer because it's like solving puzzles every day! 🧩 There's something magical about turning ideas into working applications. Plus, the tech field is always evolving, so there's always something new to learn. It never gets boring!",
      ],
    },
  };

  const suggestedQuestions = [
    "Tell me about yourself",
    "What are your technical skills?",
    "Show me your projects",
    "What certifications do you have?",
    "Where do you work?",
    "What's your educational background?",
    "Download my CV",
    "How can I contact you?",
    "What are your future goals?",
    "Tell me about your frontend skills",
  ];

  const greetings = [
    "Hey! I'm Ushan 👋 Nice to meet you! I'm a full-stack developer passionate about creating amazing web experiences. What would you like to know about me?",
    "Hi there! I'm Ushan Mihiranga 🚀 A software engineer who loves building cool stuff with React and Node.js. How can I help you today?",
    "Hello! I'm Ushan ✨ Currently working as a Full Stack Developer at FonixEdu Pvt Ltd by Atlas Axillia and studying Software Engineering at SLIIT. What brings you here?",
  ];

  const findBestMatch = useCallback((input, threshold = 0.3) => {
    const inputLower = input.toLowerCase();
    let bestMatch = null;
    let highestScore = 0;

    Object.entries(knowledgeBase).forEach(([category, data]) => {
      data.keywords.forEach((keyword) => {
        const score = calculateSimilarity(inputLower, keyword);
        if (score > highestScore && score >= threshold) {
          highestScore = score;
          bestMatch = { category, score, data };
        }
      });
    });

    return bestMatch;
  }, []);

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const generateResponse = useCallback(
    (input) => {
      const match = findBestMatch(input);

      if (match) {
        // Randomly select one response from the responses array
        const responses = match.data.responses;
        const selectedResponse = responses[Math.floor(Math.random() * responses.length)];

        const contextualResponse = addContextualInfo(
          selectedResponse,
          match.category
        );
        return {
          content: contextualResponse,
          category: match.category,
          confidence: match.score,
        };
      }

      return {
        content: `I'd love to help you learn about Ushan! 🤔 I can tell you about:<br/>• His technical skills and expertise<br/>• Professional experience and projects<br/>• Educational background<br/>• Contact information<br/><br/>Try asking: "${suggestedQuestions[
          Math.floor(Math.random() * suggestedQuestions.length)
        ]
          }"`,
        category: "help",
        confidence: 0,
      };
    },
    [findBestMatch]
  );

  const addContextualInfo = (response, category) => {
    const contextualTips = {
      skills:
        "<br/><br/>💡 <em>Want to see these skills in action? Ask about my projects!</em>",
      projects:
        "<br/><br/>🔍 <em>Interested in a specific project? I can provide GitHub links and more details!</em>",
      experience:
        "<br/><br/>📈 <em>Want to know more about my role or the technologies I use? Just ask!</em>",
    };

    return response + (contextualTips[category] || "");
  };

  const simulateTyping = useCallback((response, callback) => {
    setIsTyping(true);
    const typingDelay = Math.min(response.length * 20, 2000);

    setTimeout(() => {
      setIsTyping(false);
      callback();
    }, typingDelay);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        role: "assistant",
        content: greetings[Math.floor(Math.random() * greetings.length)],
        timestamp: new Date(),
        category: "greeting",
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText = null) => {
    const text = messageText || inputMessage.trim();
    if (!text) return;

    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    setShowSuggestions(false);

    setConversationContext((prev) => [...prev.slice(-4), text]);

    simulateTyping(text, () => {
      const response = generateResponse(text);

      const botResponse = {
        role: "assistant",
        content: response.content,
        timestamp: new Date(),
        category: response.category,
        confidence: response.confidence,
      };

      setMessages((prev) => [...prev, botResponse]);
      setIsLoading(false);
    });
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleFeedback = (messageIndex, isPositive) => {
    setMessages((prev) =>
      prev.map((msg, idx) =>
        idx === messageIndex
          ? { ...msg, feedback: isPositive ? "positive" : "negative" }
          : msg
      )
    );
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="chatbot-container">
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="chat-button">
          <ChatIcon />
        </button>
      )}

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="header-content">
              <div className="avatar-container">✨</div>
              <div>
                <h3 className="header-title">Ushan's AI Assistant</h3>
                <p className="header-subtitle">Ask me anything about Ushan!</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="close-button">
              <CloseIcon />
            </button>
          </div>

          <div className="messages-container">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`message-wrapper ${message.role === "user" ? "justify-end" : "justify-start"
                  }`}
              >
                <div
                  className={`message-content ${message.role === "user" ? "user-message" : "bot-message"
                    }`}
                >
                  {message.role === "assistant" && (
                    <div className="message-meta">
                      <BotIcon />
                      <span className="meta-text">Assistant</span>
                      <span className="meta-text">🕐</span>
                      <span className="meta-text">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  )}

                  <div className="message-body">
                    {message.role === "user" && (
                      <div className="message-meta user-meta">
                        <UserIcon />
                        <span className="meta-text">
                          {formatTime(message.timestamp)}
                        </span>
                      </div>
                    )}
                    <div
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  </div>

                  {message.role === "assistant" && !message.feedback && (
                    <div className="feedback-buttons">
                      <button
                        onClick={() => handleFeedback(index, true)}
                        className="feedback-button thumbs-up"
                      >
                        <ThumbsUpIcon />
                      </button>
                      <button
                        onClick={() => handleFeedback(index, false)}
                        className="feedback-button thumbs-down"
                      >
                        <ThumbsDownIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="message-wrapper justify-start">
                <div className="message-body bot-message">
                  <div className="typing-indicator">
                    <div className="dot"></div>
                    <div className="dot dot-delay-1"></div>
                    <div className="dot dot-delay-2"></div>
                  </div>
                </div>
              </div>
            )}

            {showSuggestions && messages.length <= 1 && (
              <div className="suggestions-container">
                <p className="suggestions-title">Quick questions:</p>
                <div className="suggestions-list">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(question)}
                      className="suggestion-button"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <div className="input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask about skills, projects, experience..."
                className="chat-input"
                disabled={isLoading}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="send-button"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedChatBot;
